import { Circle, Defs, Ellipse, Path, RadialGradient, Stop, Svg } from '../../svg';

/**
 * Reddit brand mark — Snoo on the orange speech bubble, drawn once with the
 * shared SVG primitives. Brand colours are fixed, so `color` is accepted for
 * the `IconComponent` shape and ignored.
 *
 * The artwork's radial shading is kept verbatim; gradients that shared a
 * stop list through `href` in the source SVG inline it here, because
 * react-native-svg has no gradient inheritance. The 216² artwork sits in a
 * square viewBox with a 10% margin on each side, so it fills its box the way
 * a lucide glyph does.
 */
export function RedditIcon({
  size = 20,
  className,
}: {
  color?: string;
  size?: number;
  strokeWidth?: number;
  className?: string;
}) {
  return (
    <Svg
      width={size}
      height={size}
      viewBox="-21.6 -21.6 259.2 259.2"
      fill="none"
      className={className}>
      <Defs>
        {/* The white parts (ears, face, head) share one highlight ramp. */}
        <RadialGradient
          id="flashka-reddit-ear-r"
          cx={169.75}
          cy={92.19}
          r={50.98}
          fx={169.75}
          fy={92.19}
          gradientTransform="matrix(1 0 0 .87 0 11.64)"
          gradientUnits="userSpaceOnUse">
          {WHITE_STOPS}
        </RadialGradient>
        <RadialGradient
          id="flashka-reddit-ear-l"
          cx={47.31}
          cy={92.19}
          r={50.98}
          fx={47.31}
          fy={92.19}
          gradientTransform="matrix(1 0 0 .87 0 11.64)"
          gradientUnits="userSpaceOnUse">
          {WHITE_STOPS}
        </RadialGradient>
        <RadialGradient
          id="flashka-reddit-face"
          cx={109.61}
          cy={85.59}
          r={153.78}
          fx={109.61}
          fy={85.59}
          gradientTransform="matrix(1 0 0 .7 0 25.56)"
          gradientUnits="userSpaceOnUse">
          {WHITE_STOPS}
        </RadialGradient>
        <RadialGradient
          id="flashka-reddit-head"
          cx={147.88}
          cy={32.94}
          r={39.77}
          fx={147.88}
          fy={32.94}
          gradientTransform="matrix(1 0 0 .98 0 .54)"
          gradientUnits="userSpaceOnUse">
          {WHITE_STOPS}
        </RadialGradient>
        <RadialGradient
          id="flashka-reddit-eye-l"
          cx={-6.01}
          cy={64.68}
          r={12.85}
          fx={-6.01}
          fy={64.68}
          gradientTransform="matrix(1.07 0 0 1.55 81.08 27.26)"
          gradientUnits="userSpaceOnUse">
          {EYE_STOPS}
        </RadialGradient>
        <RadialGradient
          id="flashka-reddit-eye-r"
          cx={-73.55}
          cy={64.68}
          r={12.85}
          fx={-73.55}
          fy={64.68}
          gradientTransform="matrix(-1.07 0 0 1.55 62.87 27.26)"
          gradientUnits="userSpaceOnUse">
          {EYE_STOPS}
        </RadialGradient>
        <RadialGradient
          id="flashka-reddit-mouth"
          cx={107.93}
          cy={166.96}
          r={45.3}
          fx={107.93}
          fy={166.96}
          gradientTransform="matrix(1 0 0 .66 0 57.4)"
          gradientUnits="userSpaceOnUse">
          <Stop offset="0" stopColor="#172e35" />
          <Stop offset="0.29" stopColor="#0e1c21" />
          <Stop offset="0.73" stopColor="#030708" />
          <Stop offset="1" stopColor="#000000" />
        </RadialGradient>
        <RadialGradient
          id="flashka-reddit-antenna"
          cx={131.31}
          cy={73.08}
          r={32.6}
          fx={131.31}
          fy={73.08}
          gradientUnits="userSpaceOnUse">
          <Stop offset="0.48" stopColor="#7a9299" />
          <Stop offset="0.67" stopColor="#172e35" />
          <Stop offset="0.75" stopColor="#000000" />
          <Stop offset="0.82" stopColor="#172e35" />
        </RadialGradient>
      </Defs>
      <Path
        fill="#FF4500"
        d="M108 0C48.35 0 0 48.35 0 108c0 29.82 12.09 56.82 31.63 76.37l-20.57 20.57C6.98 209.02 9.87 216 15.64 216H108c59.65 0 108-48.35 108-108S167.65 0 108 0Z"
      />
      <Circle cx={169.22} cy={106.98} r={25.22} fill="url(#flashka-reddit-ear-r)" />
      <Circle cx={46.78} cy={106.98} r={25.22} fill="url(#flashka-reddit-ear-l)" />
      <Ellipse cx={108.06} cy={128.64} rx={72} ry={54} fill="url(#flashka-reddit-face)" />
      <Path
        fill="url(#flashka-reddit-eye-l)"
        d="M86.78 123.48c-.42 9.08-6.49 12.38-13.56 12.38s-12.46-4.93-12.04-14.01c.42-9.08 6.49-15.02 13.56-15.02s12.46 7.58 12.04 16.66Z"
      />
      <Path
        fill="url(#flashka-reddit-eye-r)"
        d="M129.35 123.48c.42 9.08 6.49 12.38 13.56 12.38s12.46-4.93 12.04-14.01c-.42-9.08-6.49-15.02-13.56-15.02s-12.46 7.58-12.04 16.66Z"
      />
      <Ellipse cx={79.63} cy={116.37} rx={2.8} ry={3.05} fill="#FFC49C" />
      <Ellipse cx={146.21} cy={116.37} rx={2.8} ry={3.05} fill="#FFC49C" />
      <Path
        fill="url(#flashka-reddit-mouth)"
        d="M108.06 142.92c-8.76 0-17.16.43-24.92 1.22-1.33.13-2.17 1.51-1.65 2.74 4.35 10.39 14.61 17.69 26.57 17.69s22.23-7.3 26.57-17.69c.52-1.23-.33-2.61-1.65-2.74-7.77-.79-16.16-1.22-24.92-1.22Z"
      />
      <Circle cx={147.49} cy={49.43} r={17.87} fill="url(#flashka-reddit-head)" />
      <Path
        fill="url(#flashka-reddit-antenna)"
        d="M107.8 76.92c-2.14 0-3.87-.89-3.87-2.27 0-16.01 13.03-29.04 29.04-29.04 2.14 0 3.87 1.73 3.87 3.87s-1.73 3.87-3.87 3.87c-11.74 0-21.29 9.55-21.29 21.29 0 1.38-1.73 2.27-3.87 2.27Z"
      />
      <Path
        fill="#842123"
        d="M62.82 122.65c.39-8.56 6.08-14.16 12.69-14.16 6.26 0 11.1 6.39 11.28 14.33.17-8.88-5.13-15.99-12.05-15.99s-13.14 6.05-13.56 15.2c-.42 9.15 4.97 13.83 12.04 13.83h.52c-6.44-.16-11.3-4.79-10.91-13.2Zm90.48 0c-.39-8.56-6.08-14.16-12.69-14.16-6.26 0-11.1 6.39-11.28 14.33-.17-8.88 5.13-15.99 12.05-15.99 7.07 0 13.14 6.05 13.56 15.2.42 9.15-4.97 13.83-12.04 13.83h-.52c6.44-.16 11.3-4.79 10.91-13.2Z"
      />
    </Svg>
  );
}

// Stop lists are ARRAYS, never fragments: react-native-svg reads a gradient's
// stops with `Children.map`, which flattens arrays but treats a fragment as
// one child with no offset/colour — the whole gradient then collapses to a
// single black stop (the face rendered black on iOS).
const stops = (list: readonly (readonly [string, string])[]) =>
  list.map(([offset, stopColor]) => (
    <Stop key={offset} offset={offset} stopColor={stopColor} />
  ));

const WHITE_STOPS = stops([
  ['0', '#feffff'],
  ['0.4', '#feffff'],
  ['0.51', '#f9fcfc'],
  ['0.62', '#edf3f5'],
  ['0.7', '#dee9ec'],
  ['0.72', '#d8e4e8'],
  ['0.76', '#ccd8df'],
  ['0.8', '#c8d5dd'],
  ['0.83', '#ccd6de'],
  ['0.85', '#d8dbe2'],
  ['0.88', '#ede3e9'],
  ['0.9', '#ffebef'],
]);

const EYE_STOPS = stops([
  ['0', '#ff6600'],
  ['0.5', '#ff4500'],
  ['0.7', '#fc4301'],
  ['0.82', '#f43f07'],
  ['0.92', '#e53812'],
  ['1', '#d4301f'],
]);
